import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft,
  Check,
  ChevronRight,
  CircleSlash,
  Filter,
  Loader2,
  MessageSquareText,
  Send,
  X,
} from 'lucide-react';
import { AskLognAIIcon } from '@lognai/plugin-ai-core-ui';
import { Badge, Button, Input, Skeleton } from '@lognai/synapse-design-system-v2';
import {
  HighchartsRenderer,
  InsightPanel,
  askAnalytics,
  drillAnalysis,
  drillMemberLabel,
  drillSubtitle,
  executeAnalysis,
  executeVisualBatch,
  formatDrillCrumb,
  formatMetric,
  resolveChartTheme,
  userFacingAnalyticsError,
  type AnalysisResult,
  type BatchResult,
  type ChartThemePayload,
  type DrillSelection,
  type VisualAnswer,
} from '@lognai/plugin-analytics-ui';
import { useCardHeaderSlot } from './card-header-slot';

/**
 * A saved analytics chart embedded as a workspace card.
 *
 * Behaviour is deliberately the same as an analytics dashboard widget — click a
 * point to drill, breadcrumb back out, ask LognAI about what is on screen — and
 * it reuses that module's drill/execute/ask helpers rather than restating them,
 * so the two surfaces cannot drift apart.
 *
 * Dashboard-only concerns are intentionally left out: cross-filtering (there is
 * no dashboard filter bar here) and persisted insights (those are keyed to a
 * dashboard and widget id). Ask LognAI is therefore ephemeral per card.
 */
export function WorkspaceChartCard({
  visualId,
  onTitle,
  zeroIsPositive = false,
}: {
  visualId: string;
  /** Reports the chart's own title so the card header can show it. */
  onTitle?: (title: string) => void;
  /** True when a zero result is a healthy outcome (risk/exposure metrics). */
  zeroIsPositive?: boolean;
}) {
  const headerSlot = useCardHeaderSlot();
  const [execution, setExecution] = useState<BatchResult>();
  const [theme, setTheme] = useState<ChartThemePayload>();
  const [failed, setFailed] = useState(false);

  // Drill state, mirroring the dashboard widget: a stack of prior results plus
  // the crumbs that describe how we got here.
  const [result, setResult] = useState<AnalysisResult>();
  const [history, setHistory] = useState<AnalysisResult[]>([]);
  const [crumbs, setCrumbs] = useState<Array<{ member: string; key: string; label: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const [askOpen, setAskOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<VisualAnswer>();
  const [askBusy, setAskBusy] = useState(false);
  const [askError, setAskError] = useState<string>();
  const [selection, setSelection] = useState<DrillSelection>();
  const [conversation, setConversation] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const askSequence = useRef(0);

  // Highcharts needs an explicit pixel height; measuring the region and feeding
  // it in is what makes the chart fit whatever size the card is dragged to.
  const regionRef = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(220);

  useLayoutEffect(() => {
    const region = regionRef.current;
    if (!region) return;
    const measure = () =>
      setChartHeight(Math.max(120, Math.floor(region.getBoundingClientRect().height - 8)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(region);
    return () => observer.disconnect();
  }, [askOpen, answer, execution]);

  useEffect(() => {
    let live = true;
    setExecution(undefined);
    setFailed(false);
    setHistory([]);
    setCrumbs([]);
    setAnswer(undefined);
    setConversation([]);
    executeVisualBatch([{ visualId }])
      .then((batch) => {
        if (!live) return;
        const next = batch.results[0];
        if (!next) return setFailed(true);
        setExecution(next);
        setResult(next.result);
        const title = next.result?.visual?.title;
        if (title) onTitle?.(title);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [visualId]);

  useEffect(() => {
    let live = true;
    resolveChartTheme()
      .then((resolved) => {
        if (live) setTheme(resolved.payload);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const drill = async (next: DrillSelection) => {
    if (!result) return;
    try {
      setBusy(true);
      setError(undefined);
      const analysis = drillAnalysis(result.analysis, next.member, next.point);
      if (!analysis) return;
      const drilled = await executeAnalysis(analysis);
      setHistory((items) => [...items, result]);
      setCrumbs((items) => [...items, { member: next.member, key: next.point.key, label: next.point.label }]);
      setResult(drilled);
      setSelection(next);
      // A drill changes what is on screen, so any prior answer no longer applies.
      askSequence.current += 1;
      setAnswer(undefined);
      setAskError(undefined);
      setConversation([]);
    } catch (cause) {
      setError(userFacingAnalyticsError(cause));
    } finally {
      setBusy(false);
    }
  };

  const goToDepth = (depth: number) => {
    if (!history.length || depth >= crumbs.length) return;
    const target = history[depth];
    if (!target) return;
    setResult(target);
    setHistory((items) => items.slice(0, depth));
    setCrumbs((items) => items.slice(0, depth));
    askSequence.current += 1;
    setSelection(undefined);
    setAnswer(undefined);
    setAskError(undefined);
    setConversation([]);
  };

  const ask = async (nextQuestion = question) => {
    const message = nextQuestion.trim();
    if (!result || !message || askBusy) return;
    const sequence = askSequence.current + 1;
    askSequence.current = sequence;
    setQuestion(message);
    setAskBusy(true);
    setAskError(undefined);
    try {
      const nextAnswer = await askAnalytics({
        message,
        activeAnalysis: result.analysis,
        selection: selection
          ? { member: selection.member, key: selection.point.key, label: selection.point.label }
          : undefined,
        history: conversation,
      });
      if (askSequence.current !== sequence) return;
      setAnswer(nextAnswer);
      setConversation((turns) =>
        [
          ...turns,
          { role: 'user' as const, content: message },
          { role: 'assistant' as const, content: nextAnswer.insight.summary },
        ].slice(-6)
      );
    } catch (cause) {
      if (askSequence.current === sequence) {
        setAskError(userFacingAnalyticsError(cause, 'The insight could not be created. Please try again.'));
      }
    } finally {
      if (askSequence.current === sequence) setAskBusy(false);
    }
  };

  const rowState = result?.meta?.rowState;
  const primaryEvidence = result?.evidence?.[0];
  const nextDrill = result?.visual.categoryMember
    ? drillMemberLabel(result.visual.categoryMember)
    : undefined;
  const subtitle = crumbs.length && result ? drillSubtitle(crumbs, result.visual.categoryMember) : undefined;

  /**
   * Insight headlines lead with the chart's own title, because on an analytics
   * dashboard the panel opens away from the chart it describes. Here the title
   * is already in the card header, so the overlay shows only what the headline
   * adds — and falls back to a plain "Insight" when it adds nothing.
   */
  const insightHeading = (() => {
    const headline = answer?.insight.headline?.trim();
    if (!headline) return 'Insight';
    const title = result?.visual?.title?.trim();
    if (!title) return headline;
    if (headline.toLowerCase() === title.toLowerCase()) return 'Insight';
    if (headline.toLowerCase().startsWith(`${title.toLowerCase()}:`)) {
      return headline.slice(title.length + 1).trim() || 'Insight';
    }
    return headline;
  })();

  const centred = (children: React.ReactNode) => (
    <div className="flex h-full min-h-[140px] flex-col items-center justify-center px-6 text-center">
      {children}
    </div>
  );

  const body = () => {
    if (failed || (execution && !execution.ok)) {
      return centred(<p className="text-xs text-muted-foreground">This chart could not be loaded.</p>);
    }
    if (rowState === 'empty') {
      return centred(
        <>
          <div className="h-1 w-10 rounded-full bg-muted" />
          <p className="mt-3 text-sm font-medium">No data for the current company</p>
          <p className="mt-1 text-xs text-muted-foreground">Try a wider date range in Analytics.</p>
        </>
      );
    }
    if (rowState === 'zero') {
      return centred(
        zeroIsPositive ? (
          <>
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-success-subtle text-success">
              <Check size={18} />
            </div>
            <p className="mt-3 text-sm font-medium">No current exposure detected</p>
          </>
        ) : (
          <>
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted text-muted-foreground">
              <CircleSlash size={18} />
            </div>
            <p className="mt-3 text-sm font-medium">Zero recorded for this period</p>
          </>
        )
      );
    }
    if (result) {
      return (
        <HighchartsRenderer
          visual={result.visual}
          onSelect={(next) => void drill(next)}
          height={chartHeight}
          compact
          theme={theme}
        />
      );
    }
    return (
      <div className="flex h-full min-h-[140px] flex-col justify-end gap-2 p-3" aria-busy="true" aria-label="Loading chart">
        <div className="flex min-h-0 flex-1 items-end gap-2">
          {[45, 70, 55, 85, 62, 74].map((height, index) => (
            <Skeleton key={index} className="w-full" style={{ height: `${height}%` }} />
          ))}
        </div>
        <Skeleton className="h-3 w-28" />
      </div>
    );
  };

  /**
   * The value and the chart's own controls, rendered onto the card's title line
   * rather than a toolbar of their own — a workspace card is short, and a
   * dedicated row cost the chart height it could not spare.
   */
  const headerControls = (
    <>
      {primaryEvidence && rowState === 'ready' && (
        <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
          {formatMetric(Number(primaryEvidence.value), primaryEvidence.unit ?? 'number')}
        </span>
      )}
      {history.length > 0 && (
        <Button
          variant="ghost"
          size="xs"
          aria-label="Back one drill level"
          title="Back"
          onClick={() => goToDepth(Math.max(0, crumbs.length - 1))}
        >
          <ArrowLeft size={14} />
        </Button>
      )}
      {!askOpen && (
        <Button
          variant="ghost"
          size="xs"
          aria-label="Ask LognAI about this chart"
          title="Ask LognAI"
          onClick={() => setAskOpen(true)}
        >
          <MessageSquareText size={14} />
        </Button>
      )}
    </>
  );

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {headerSlot ? createPortal(headerControls, headerSlot) : null}

      {crumbs.length > 0 && result && (
        <nav
          className="flex min-h-[30px] shrink-0 items-center gap-1 overflow-x-auto border-y border-border bg-muted/20 px-3 py-1 text-xs"
          aria-label="Chart drilldown"
          title={subtitle}
        >
          <Button className="h-6 shrink-0 px-1.5 text-xs" variant="ghost" size="xs" onClick={() => goToDepth(0)}>
            All
          </Button>
          {crumbs.map((crumb, index) => (
            <span className="inline-flex shrink-0 items-center gap-1" key={`${crumb.member}-${index}`}>
              <ChevronRight size={12} className="text-muted-foreground" />
              {index < crumbs.length - 1 ? (
                <button
                  className="max-w-[130px] truncate px-1 py-0.5 font-medium hover:text-primary"
                  title={formatDrillCrumb(crumb)}
                  onClick={() => goToDepth(index + 1)}
                >
                  {formatDrillCrumb(crumb)}
                </button>
              ) : (
                <span className="max-w-[130px] truncate px-1 py-0.5 font-medium" title={formatDrillCrumb(crumb)}>
                  {formatDrillCrumb(crumb)}
                </span>
              )}
            </span>
          ))}
        </nav>
      )}

      <div ref={regionRef} className="relative min-h-0 flex-1 overflow-hidden px-2">
        {body()}
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center bg-surface/75">
            <Loader2 className="animate-spin text-primary" />
          </div>
        )}

        {/* The answer overlays the chart rather than stacking above it. A
            workspace card has a fixed height, so stacking would squeeze the
            chart to its minimum and make both unreadable — the analytics
            dashboard avoids this by rendering the insight OUTSIDE the card,
            which a grid cell cannot do. */}
        {answer && (
          <div className="absolute inset-0 z-10 flex flex-col overflow-hidden bg-surface">
            <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-1.5">
              <span className="truncate text-xs font-semibold text-foreground" title={insightHeading}>
                {insightHeading}
              </span>
              <Button
                size="xs"
                variant="ghost"
                aria-label="Close insight"
                title="Back to chart"
                onClick={() => setAnswer(undefined)}
              >
                <X size={14} />
              </Button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <InsightPanel
                insight={answer.insight}
                hideHeadline
                evidence={answer.evidence}
                trace={answer.trace}
                onSuggestedQuestion={(suggestion) => void ask(suggestion)}
                disabled={askBusy}
              />
            </div>
          </div>
        )}
      </div>

      {nextDrill && rowState === 'ready' && !askOpen && (
        <div className="flex min-h-[28px] shrink-0 items-center px-3 pb-1">
          <Badge variant="outline">
            <Filter size={11} /> Drill to {nextDrill}
          </Badge>
        </div>
      )}

      {error && (
        <div className="shrink-0 border-t border-destructive-border bg-destructive-subtle px-3 py-1.5 text-xs text-destructive-foreground">
          {error}
        </div>
      )}

      {askOpen && (
        <form
          className="flex min-w-0 shrink-0 items-center gap-2 border-t border-border bg-surface px-3 py-2"
          onSubmit={(event) => {
            event.preventDefault();
            void ask();
          }}
        >
          <AskLognAIIcon size={14} className="shrink-0 text-primary" />
          <Input
            className="min-w-0 flex-1"
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="Ask why, compare, or find the main driver"
            maxLength={1000}
            disabled={!result || askBusy}
          />
          <Button className="shrink-0" type="submit" size="sm" aria-label="Send question" disabled={!result || !question.trim() || askBusy}>
            {askBusy ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />}
          </Button>
          <Button type="button" size="sm" variant="ghost" title="Close" aria-label="Close Ask LognAI" onClick={() => setAskOpen(false)}>
            <X size={15} />
          </Button>
        </form>
      )}

      {askError && (
        <div className="shrink-0 border-t border-destructive-border bg-destructive-subtle px-3 py-1.5 text-xs text-destructive-foreground">
          {askError}
        </div>
      )}

    </div>
  );
}
